<script lang="ts">
	import type { Product } from "../../../core/schema/Product.js";
	import { ProductPhotoPosition } from "../../../core/schema/ProductPhotoPosition.js";
	import DynamicPicture from "./Picture/DynamicPicture.svelte";
	import Content from "./Content.svelte";
	import type { ActionReturn } from "svelte/action";
	import { gsap } from "gsap";
	import { querySizeMappings } from "../utils/querySizeMappings.js";
	import { nbspify } from "../../../core/utils/nbspify.js";

	export let product: Product;

	let image: HTMLElement | null = null;
	let content: HTMLElement | null = null;
	export function animation(node: HTMLElement): ActionReturn {
		let hasAnimated = false;

		node.style.opacity = "0";

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !hasAnimated) {
						node.style.opacity = "100%";
						if (content) {
							gsap.set(content, { opacity: 0, x: product.photoPosition === ProductPhotoPosition.Left ? -100 : 100 });
						}
						if (image) {
							gsap.set(image, { opacity: 0, x: product.photoPosition === ProductPhotoPosition.Left ? 200 : -200 });
						}
						const timeline = gsap.timeline();

						const order = window.innerWidth <= querySizeMappings.lg ? [image, content] : [content, image];
						for (const item of order) {
							if (!item) {
								continue;
							}
							timeline.to(item, { opacity: 1, x: 0, duration: 0.75 });
						}

						hasAnimated = true;
					}
				});
			},
			{ threshold: 0.25 },
		);

		observer.observe(node);

		return {
			destroy(): void {
				observer.disconnect();
			},
		};
	}
</script>

<a
	use:animation
	role={product.externalUrl ? "link" : "none"}
	href={product.externalUrl}
	target="_blank"
	class="flex flex-col-reverse md:px-5 md:py-7 lg:min-h-screen lg:flex-row lg:px-14 lg:py-16"
	class:lg:flex-row-reverse={product.photoPosition === ProductPhotoPosition.Left}
>
	<div
		bind:this={content}
		class="relative z-10 flex w-full flex-col justify-between px-7 py-14 lg:w-[36rem] lg:px-14 lg:py-16 xl:w-[48rem]"
	>
		<div class="absolute inset-0 -z-10 bg-white/90 backdrop-blur-xl"></div>
		<h2 class="text-md mx-0 font-semibold leading-[1.13] sm:text-xl md:text-2xl lg:text-xl xl:text-4xl">
			{nbspify(product.name)}
		</h2>
		<Content content={product.content} customHeadlinesFontWeight={true} />
	</div>

	<div
		bind:this={image}
		class="relative lg:flex-1 {product.photoPosition === ProductPhotoPosition.Left ? 'lg:-right-14' : 'lg:-left-14'}"
	>
		<DynamicPicture
			image={product.primaryImage}
			alt={product.name}
			width={900}
			height={666}
			imgClass="h-full w-full object-cover lg:object-contain lg:absolute {product.photoPosition ===
			ProductPhotoPosition.Left
				? 'lg:object-right'
				: 'lg:object-left'}"
			bucket="storage"
			sourcesets={{
				"<sm": [0, 450],
				"<md": [0, 456],
				"<lg": [0, 535],
			}}
		/>
	</div>
</a>
